<template>
  <json-viewer :data="{ defaultValues, ourData, openapiSchema }" closed />
  <table v-if="debug">
    <thead>
      <tr>
        <th>Key</th>
        <th>required</th>
        <th>value</th>
        <th>Openapi</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="[key, openapi] in schema" :key="key">
        <td>{{ key }}</td>
        <td>{{ required.indexOf(key) !== -1 }}</td>
        <td>{{ ourData[key] ?? undefined }}</td>
        <td>{{ openapi }}</td>
      </tr>
    </tbody>
  </table>
  <div class="form-elements">
    <InputElement
      v-for="[key, openapi] in schema"
      :key="key"
      :required="required.indexOf(key) !== -1"
      :openapi="openapi"
      :multiline="multilineFields.indexOf(key) !== -1"
      :name="key"
      @update:modelValue="updateItem(key, $event, true)"
      :modelValue="ourData[key]"
    />
    <slot name="form" :data="ourData" :updateItem="updateItem" />
  </div>
</template>

<script setup lang="ts" generic="DATA extends dict">
import { defineComponent, ref, computed } from "vue";

import InputElement from "./InputElement.vue";
import type { dict } from "@/types";
import JsonViewer from "@/components/JsonViewer.vue";

const debug = ref<boolean>(false); // can be set in the inspector for debugging

defineComponent({
  name: "OpenApiForm",
});

const props = withDefaults(
  defineProps<{
    openapiSchema: object;
    ignoredFields: string[];
    firstFields: string[];
    multilineFields: string[];
    defaultValues: DATA;
    modelValue: DATA;
  }>(),
  {
    ignoredFields: () => [],
    firstFields: () => [],
    multilineFields: () => [],
    defaultValues: () => ({}),
    modelValue: () => ({}),
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: dict): void;
}>();
// Two special cases:
// - type="null": those are server filled, we don't need them.
// - name="contained_in_object": this is the referrer, this will be filled from the opening page via prop.

const schema = computed<[string, object][]>(() =>
  props.firstFields
    .concat(
      Object.entries(props.openapiSchema.properties)
        .map(([k, v]) => k)
        .filter((k) => props.firstFields.indexOf(k) === -1)
    )
    .filter((k) => props.ignoredFields.indexOf(k) === -1)
    .map((k: string): [string, { [index: string]: any }] => [
      k,
      props.openapiSchema.properties,
    ])
    .map(([k, properties]: [string, { [index: string]: any }]) => [
      k,
      properties[k],
    ])
);
const required = computed<string[]>(
  () => props.openapiSchema.required || []
);

// initial data for this basically makes a key for every element we wanna have a field for.
// also add everything from our default-values prop.
const defaultData = computed<DATA>(() =>
  schema.value
    .map(([key, _]) => key)
    .reduce((prev: dict, newKey: string): dict => {
      prev[newKey] =
        newKey in props.defaultValues
          ? props.defaultValues[newKey as any]
          : null;
      return prev;
    }, props.defaultValues)
);
const ourData = ref<DATA>(defaultData.value);
const updateItem = (
  key: string,
  value: any,
  broadcastUpdate = true
): void => {
  console.log("form data [" + key + "] will change:", value);
  ourData.value[key] = value;
  console.log(
    "form data [" + key + "] has changed, full data:",
    ourData.value
  );
  if (broadcastUpdate) {
    emit("update:modelValue", ourData.value);
  }
};
</script>

<style scoped lang="scss"></style>
